import React, { useState, useEffect, useMemo } from "react";
import { Box, SvgIcon, Typography } from "@mui/material";
import { ReactComponent as MicrophoneIcon } from "../assets/microphone.svg";
import GuideWaveformOrb, {
  GUIDE_VISUAL_STATES,
  hexToRgbGuide,
} from "./GuideWaveformOrb";
import { normalizeGuideStateValue } from "./unityWebViewer/guideRuntime";
import {
  viewerActionButtonLabelSx,
  viewerActionButtonSx,
  viewerPanelSurfaceSx,
} from "./unityWebViewer/panelStyles";

// ============================================
// UTILITY FUNCTIONS
// ============================================

const normalizeState = (s) => {
  const normalizedState = normalizeGuideStateValue(s);
  if (!normalizedState) return "DEFAULT";
  const up = String(normalizedState).toUpperCase();
  if (up.includes("LISTEN")) return "LISTENING";
  if (up.includes("TALK") || up.includes("SPEAK")) return "TALKING";
  if (up.includes("THINK") || up.includes("PROCESS")) return "THINKING";
  if (
    up.includes("WAIT") ||
    up.includes("PAUSE") ||
    up.includes("IDLE") ||
    up.includes("INSIGHT_COMPLETED") ||
    up.includes("INSIGHT_CANCELLED") ||
    up.includes("GUIDE_STARTED") ||
    up.includes("INSIGHT_STARTED")
  ) {
    return "WAITING";
  }
  if (up.includes("READY")) return "DEFAULT";
  return "DEFAULT";
};

const SUBTITLE_MIN_WORDS = 10;
const SUBTITLE_TARGET_WORDS = 15;
const SUBTITLE_MAX_WORDS = 15;
const ESTIMATED_WORD_MS = 360;
const ESTIMATED_CHAR_MS = 24;
const MIN_SUBTITLE_CHUNK_MS = 1800;
const MAX_SUBTITLE_CHUNK_MS = 7000;
const SUBTITLE_END_PAUSE_MS = 650;

const splitIntoSubtitleChunks = (text) => {
  const words = String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return [];
  if (words.length <= SUBTITLE_MAX_WORDS) return [words.join(" ")];

  const chunks = [];
  let current = [];

  words.forEach((word) => {
    current.push(word);

    const endsPhrase = /[.!?;:]$/.test(word);
    const shouldEndAtPunctuation =
      current.length >= SUBTITLE_MIN_WORDS && endsPhrase;
    const shouldEndAtTarget = current.length >= SUBTITLE_TARGET_WORDS;
    const shouldEndAtMax = current.length >= SUBTITLE_MAX_WORDS;

    if (shouldEndAtPunctuation || shouldEndAtTarget || shouldEndAtMax) {
      chunks.push(current);
      current = [];
    }
  });

  if (current.length > 0) {
    chunks.push(current);
  }

  if (chunks.length > 1) {
    const last = chunks[chunks.length - 1];
    const previous = chunks[chunks.length - 2];

    while (
      last.length < SUBTITLE_MIN_WORDS &&
      previous.length > SUBTITLE_MIN_WORDS
    ) {
      last.unshift(previous.pop());
    }

    if (
      last.length < SUBTITLE_MIN_WORDS &&
      previous.length + last.length <= SUBTITLE_MAX_WORDS
    ) {
      chunks.splice(chunks.length - 2, 2, [...previous, ...last]);
    }
  }

  return chunks.map((chunk) => chunk.join(" "));
};

const estimateSubtitleChunkWeight = (chunk) => {
  const words = String(chunk || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const characterCount = words.join("").length;
  return Math.max(words.length * ESTIMATED_WORD_MS, characterCount * ESTIMATED_CHAR_MS, 1);
};

const estimateSubtitleChunkDuration = (chunk, isLastChunk = false) => {
  const estimatedMs = estimateSubtitleChunkWeight(chunk);

  const clampedMs = Math.min(
    Math.max(estimatedMs, MIN_SUBTITLE_CHUNK_MS),
    MAX_SUBTITLE_CHUNK_MS,
  );

  return isLastChunk ? clampedMs + SUBTITLE_END_PAUSE_MS : clampedMs;
};

const getSubtitleChunkDurations = (chunks, audioLengthSeconds) => {
  const audioLengthMs = Number(audioLengthSeconds) * 1000;
  if (Number.isFinite(audioLengthMs) && audioLengthMs > 0 && chunks.length > 0) {
    const weights = chunks.map(estimateSubtitleChunkWeight);
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

    if (totalWeight > 0) {
      return weights.map((weight) => Math.max(50, (audioLengthMs * weight) / totalWeight));
    }
  }

  return chunks.map((chunk, index) =>
    estimateSubtitleChunkDuration(chunk, index === chunks.length - 1),
  );
};

const useTimedSubtitleChunks = (text, isActive, audioLengthSeconds = 0) => {
  const chunks = useMemo(() => {
    if (!text) return [];
    return splitIntoSubtitleChunks(text);
  }, [text]);

  const chunkDurations = useMemo(
    () => getSubtitleChunkDurations(chunks, audioLengthSeconds),
    [audioLengthSeconds, chunks],
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [text]);

  useEffect(() => {
    if (!isActive) return undefined;

    setCurrentIndex(0);

    if (chunks.length <= 1) return undefined;

    let timeoutId = null;

    const scheduleNextChunk = (index) => {
      if (index >= chunks.length - 1) return;

      timeoutId = setTimeout(() => {
        const nextIndex = index + 1;
        setCurrentIndex(nextIndex);
        scheduleNextChunk(nextIndex);
      }, chunkDurations[index]);
    };

    scheduleNextChunk(0);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isActive, chunks.length, chunkDurations]);

  if (!text || chunks.length === 0) {
    return { displayText: "", progress: 0, sentenceCount: 0, currentSentence: 0 };
  }

  const safeIndex = Math.min(currentIndex, chunks.length - 1);
  const displayText = chunks[safeIndex] || chunks[0];
  const progress = chunks.length > 1 ? (safeIndex / (chunks.length - 1)) * 100 : 100;

  return { displayText, progress, sentenceCount: chunks.length, currentSentence: safeIndex + 1 };
};

// ============================================
// ORB
// ============================================

const ORB_SIZE = 131;
const ORB_STAGE_HEIGHT = 141;
const ORB_HOVER_TOP_OFFSET = 26;
const ORB_STAGE_LIFT = 18;
const QUESTION_BADGE_SIZE = 44;
const QUESTION_BADGE_HOVER_WIDTH = 154;
const REPLAY_BADGE_HOVER_WIDTH = 96;
const REPLAY_BADGE_RIGHT_OFFSET = 4;

const ReplayGuideIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path
      d="M12 5a7 7 0 1 1-6.32 10h2.24A5 5 0 1 0 12 7H8.8l2.1 2.1-1.42 1.42L4.96 6l4.52-4.52L10.9 2.9 8.8 5H12Z"
      fill="currentColor"
    />
  </SvgIcon>
);

const getOrbBadgeSx = (isExpanded, hoverWidth = QUESTION_BADGE_HOVER_WIDTH) => ({
  width: isExpanded ? hoverWidth : QUESTION_BADGE_SIZE,
  height: QUESTION_BADGE_SIZE,
  px: isExpanded ? 1.65 : 0,
  py: 0,
  m: 0,
  minWidth: 0,
  boxSizing: "border-box",
  borderRadius: "999px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  transform: "translate(-50%, -42%)",
  border: "1px solid rgba(210, 255, 249, 0.28)",
  background:
    "var(--recording-cta-gradient, linear-gradient(135deg, rgba(133, 245, 236, 1) 0%, rgba(90, 222, 212, 1) 100%))",
  color: "var(--recording-cta-text, #083033)",
  boxShadow:
    "var(--recording-cta-shadow, 0 10px 24px rgba(77, 196, 187, 0.35))",
  backdropFilter:
    "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
  WebkitBackdropFilter:
    "blur(var(--surface-blur, 10px)) saturate(var(--surface-saturate, 120%))",
  appearance: "none",
  fontFamily: "inherit",
  cursor: "pointer",
  pointerEvents: "auto",
  zIndex: 3,
  animation: "questionBadgePop 260ms cubic-bezier(0.2, 0.9, 0.2, 1)",
  overflow: "hidden",
  transition:
    "top 180ms ease, width 180ms ease, padding 180ms ease, background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease, transform 160ms ease",
  "&:hover": {
    background:
      "var(--recording-cta-gradient-hover, linear-gradient(135deg, rgba(161, 248, 241, 1) 0%, rgba(109, 234, 224, 1) 100%))",
    borderColor: "rgba(210, 255, 249, 0.42)",
    boxShadow:
      "0 0 20px rgba(89, 218, 230, 0.24), var(--recording-cta-shadow, 0 10px 24px rgba(77, 196, 187, 0.35))",
    color: "var(--recording-cta-text, #083033)",
  },
  "&:active": {
    boxShadow:
      "0 2px 5px rgba(0, 48, 58, 0.32), inset 0 1px 2px rgba(0, 0, 0, 0.22)",
  },
  "&:focus-visible": {
    outline: "2px solid rgba(161, 241, 224, 0.82)",
    outlineOffset: 3,
  },
  "@keyframes questionBadgePop": {
    "0%": {
      opacity: 0,
      transform: "translate(-50%, -18%) scale(0.78)",
    },
    "100%": {
      opacity: 1,
      transform: "translate(-50%, -42%) scale(1)",
    },
  },
  "&::after": {
    content: "none",
  },
});

// ============================================
// BLOB PROTOTYPE
// ============================================

const BlobPrototype = ({
  guideState,
  guideStateText,
  onStartGuide,
  onRepeatInsight,
  isRecording = false,
  onStopRecording,
  questionState = "idle",
  questionEnabled = false,
  questionDisplayText = "",
  onAskQuestion,
  microphonePermissionDenied = false,
  onEnableMicrophone,
  defaultMessage = "",
  showStartGuideButton = false,
  guideAudioLevel = 0,
  microphoneLevel = 0,
}) => {
  const guideVisualState = normalizeState(guideState?.guideState);
  const normalizedGuideState = normalizeGuideStateValue(guideState?.guideState);
  const state =
    questionState === "recording"
      ? "LISTENING"
      : questionState === "processing"
      ? "THINKING"
      : questionState === "speaking"
      ? "TALKING"
      : questionState === "error"
      ? "WAITING"
      : guideVisualState;
  const guideStateMessage =
    typeof guideState?.script === "string"
      ? guideState.script.trim()
      : typeof guideState?.data === "string"
      ? guideState.data.trim()
      : "";
  const guideTextMessage =
    typeof guideStateText === "string" ? guideStateText.trim() : "";
  const message = guideStateMessage || guideTextMessage || "";
  const hasExplicitEmptyScript =
    guideState &&
    Object.prototype.hasOwnProperty.call(guideState, "script") &&
    !guideStateMessage;
  const [rememberedGuideMessage, setRememberedGuideMessage] = useState("");

  useEffect(() => {
    if (message) {
      setRememberedGuideMessage(message);
    } else if (hasExplicitEmptyScript) {
      setRememberedGuideMessage("");
    }
  }, [hasExplicitEmptyScript, message]);

  const persistentGuideMessage = hasExplicitEmptyScript
    ? ""
    : message || rememberedGuideMessage;
  const { displayText } = useTimedSubtitleChunks(
    persistentGuideMessage,
    state === "TALKING",
    guideState?.audioLength,
  );
  const visibleGuideMessage = displayText || persistentGuideMessage;
  const stateColor =
    GUIDE_VISUAL_STATES[state]?.hex || GUIDE_VISUAL_STATES.DEFAULT.hex;
  const stateRgb = hexToRgbGuide(stateColor);
  const [showWaitingHint, setShowWaitingHint] = useState(false);
  const [showQuestionHoverHint, setShowQuestionHoverHint] = useState(false);
  const [showGuideActionHoverHint, setShowGuideActionHoverHint] =
    useState(false);

  const showQuestionRecordingUI = questionState === "recording";
  const showRecordingUI = state === "LISTENING" && (isRecording || showQuestionRecordingUI);
  const orbActionEnabled =
    !showRecordingUI &&
    questionEnabled &&
    (questionState === "idle" || questionState === "error");
  const showQuestionTapBadge =
    !showRecordingUI &&
    questionEnabled &&
    (questionState === "idle" || questionState === "error");
  const canStartGuide = Boolean(showStartGuideButton && onStartGuide);
  const insightReplayAvailable = [
    "INSIGHT_STARTED",
    "TALKING",
    "LISTENING",
    "THINKING",
    "WAITING",
  ].some((stateName) => normalizedGuideState.includes(stateName));
  const canRepeatInsight = Boolean(
    !showStartGuideButton && onRepeatInsight && insightReplayAvailable,
  );
  const showStartGuideInlineButton =
    !showRecordingUI &&
    canStartGuide &&
    (questionState === "idle" || questionState === "error");
  const showGuideActionButton =
    !showRecordingUI &&
    canRepeatInsight &&
    (questionState === "idle" || questionState === "error") &&
    !normalizedGuideState.includes("INSIGHT_COMPLETED") &&
    !normalizedGuideState.includes("INSIGHT_CANCELLED");
  const showMicrophonePermissionButton =
    !showRecordingUI &&
    microphonePermissionDenied &&
    typeof onEnableMicrophone === "function";

  useEffect(() => {
    if (guideVisualState !== "WAITING") {
      setShowWaitingHint(false);
      return undefined;
    }

    const timerId = setTimeout(() => {
      setShowWaitingHint(true);
    }, 30000);

    return () => clearTimeout(timerId);
  }, [guideVisualState]);

  useEffect(() => {
    if (!showQuestionTapBadge) {
      setShowQuestionHoverHint(false);
    }
  }, [showQuestionTapBadge]);

  useEffect(() => {
    if (!showGuideActionButton) {
      setShowGuideActionHoverHint(false);
    }
  }, [showGuideActionButton]);

  const handleQuestionClick = () => {
    if (
      questionEnabled &&
      (questionState === "idle" || questionState === "error") &&
      onAskQuestion
    ) {
      onAskQuestion();
    }
  };

  const handleQuestionBadgeClick = (event) => {
    event.stopPropagation();
    handleQuestionClick();
  };

  const handleGuideActionBadgeClick = (event) => {
    event.stopPropagation();
    onRepeatInsight();
  };

  const handleStartGuideButtonClick = (event) => {
    event.stopPropagation();
    onStartGuide();
  };

  const handleOrbClick = (event) => {
    if (!orbActionEnabled) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - bounds.left;
    const clickY = event.clientY - bounds.top;
    const orbRadius = ORB_SIZE / 2;
    const orbCenterX = bounds.width / 2;
    const orbCenterY = ORB_HOVER_TOP_OFFSET + orbRadius;
    const distanceFromCenter = Math.hypot(clickX - orbCenterX, clickY - orbCenterY);

    if (distanceFromCenter > orbRadius) {
      return;
    }

    handleQuestionClick();
  };

  const getDisplayMessage = () => {
    if (questionState === "recording") return "Ask about what you are seeing.";
    if (questionState === "processing") return "Thinking about your question...";
    if (questionState === "speaking" && questionDisplayText) return questionDisplayText;
    if (questionState === "error" && questionDisplayText) return questionDisplayText;
    if (state === "TALKING" && displayText) return displayText;
    if (microphonePermissionDenied && state === "LISTENING") {
      return visibleGuideMessage || defaultMessage;
    }
    if (state === "LISTENING") return visibleGuideMessage || "I'm listening...";
    if (state === "THINKING") return "Thinking...";
    if (state === "WAITING") return visibleGuideMessage || defaultMessage;
    return visibleGuideMessage || defaultMessage;
  };
  const displayMessage = getDisplayMessage();
  const hasDisplayMessage = Boolean(displayMessage.trim());
  const blobAudioLevel =
    state === "LISTENING"
      ? microphoneLevel
      : state === "TALKING"
      ? guideAudioLevel
      : 0;
  const guideActionLabel = "Replay";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        px: 0,
        py: 2,
        minHeight: showRecordingUI && hasDisplayMessage ? 340 : 280,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        pointerEvents: "auto",
        position: "relative",
      }}
    >
      {/* Orb */}
      <Box
        onClick={handleOrbClick}
        onMouseEnter={showQuestionTapBadge ? () => setShowQuestionHoverHint(true) : undefined}
        onMouseLeave={showQuestionTapBadge ? () => setShowQuestionHoverHint(false) : undefined}
        sx={{
          mb: 0.75,
          height: ORB_STAGE_HEIGHT,
          width: "100%",
          maxWidth: "min(280px, 100%)",
          mt: `-${ORB_HOVER_TOP_OFFSET + ORB_STAGE_LIFT}px`,
          pt: `${ORB_HOVER_TOP_OFFSET}px`,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          position: "relative",
          overflow: "visible",
          cursor: orbActionEnabled ? "pointer" : "default",
        }}
      >
        {showQuestionTapBadge && (
          <Box
            component="button"
            type="button"
            aria-label="Ask a question"
            onClick={handleQuestionBadgeClick}
            onFocus={() => setShowQuestionHoverHint(true)}
            onBlur={() => setShowQuestionHoverHint(false)}
            sx={{
              position: "absolute",
              top: showQuestionHoverHint ? 0 : 8,
              left: "50%",
              ...getOrbBadgeSx(showQuestionHoverHint),
            }}
          >
            <Typography
              component="span"
              className="control-label"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: showQuestionHoverHint ? "0.82rem" : "1.2rem",
                fontWeight: 800,
                lineHeight: 1,
                letterSpacing: 0,
                whiteSpace: "nowrap",
                transition: "font-size 180ms ease, transform 180ms ease",
              }}
            >
              {showQuestionHoverHint ? "Ask a question" : "?"}
            </Typography>
          </Box>
        )}

        <GuideWaveformOrb
          state={state}
          audioLevel={blobAudioLevel}
          size={ORB_SIZE}
        />

        {showGuideActionButton && (
          <Box
            component="button"
            type="button"
            aria-label={guideActionLabel}
            onClick={handleGuideActionBadgeClick}
            onFocus={() => setShowGuideActionHoverHint(true)}
            onBlur={() => setShowGuideActionHoverHint(false)}
            onMouseEnter={() => setShowGuideActionHoverHint(true)}
            onMouseLeave={() => setShowGuideActionHoverHint(false)}
            sx={{
              position: "absolute",
              top: ORB_HOVER_TOP_OFFSET + ORB_SIZE / 2,
              right: REPLAY_BADGE_RIGHT_OFFSET,
              ...getOrbBadgeSx(
                showGuideActionHoverHint,
                showStartGuideButton
                  ? QUESTION_BADGE_HOVER_WIDTH
                  : REPLAY_BADGE_HOVER_WIDTH,
              ),
              transform: "translateY(-50%)",
              animation: "replayBadgePop 220ms cubic-bezier(0.2, 0.9, 0.2, 1)",
              "@keyframes replayBadgePop": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(calc(-50% - 6px)) scale(0.86)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(-50%) scale(1)",
                },
              },
            }}
          >
            <Typography
              component="span"
              className="control-label"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                fontSize: showGuideActionHoverHint ? "0.82rem" : "1.2rem",
                fontWeight: 800,
                lineHeight: 1,
                letterSpacing: 0,
                whiteSpace: "nowrap",
                transition: "font-size 180ms ease, transform 180ms ease",
                "& svg": {
                  fontSize: showGuideActionHoverHint ? 16 : 22,
                },
              }}
            >
              {showGuideActionHoverHint ? guideActionLabel : <ReplayGuideIcon />}
            </Typography>
          </Box>
        )}
      </Box>

      {showStartGuideInlineButton && (
        <Box
          component="button"
          type="button"
          aria-label="Start guide"
          onClick={handleStartGuideButtonClick}
          sx={{
            mt: 0.35,
            mb: hasDisplayMessage ? 0.75 : 0,
            px: 1.65,
            py: 0.7,
            minHeight: 34,
            borderRadius: "999px",
            border: "1px solid rgba(210, 255, 249, 0.28)",
            background:
              "var(--recording-cta-gradient, linear-gradient(135deg, rgba(133, 245, 236, 1) 0%, rgba(90, 222, 212, 1) 100%))",
            color: "var(--recording-cta-text, #083033)",
            boxShadow:
              "var(--recording-cta-shadow, 0 10px 24px rgba(77, 196, 187, 0.35))",
            appearance: "none",
            fontFamily: "inherit",
            cursor: "pointer",
            pointerEvents: "auto",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
            transition:
              "background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
            "&:hover": {
              transform: "translateY(-1px)",
              background:
                "var(--recording-cta-gradient-hover, linear-gradient(135deg, rgba(161, 248, 241, 1) 0%, rgba(109, 234, 224, 1) 100%))",
              borderColor: "rgba(210, 255, 249, 0.42)",
              boxShadow:
                "0 0 20px rgba(89, 218, 230, 0.24), var(--recording-cta-shadow, 0 10px 24px rgba(77, 196, 187, 0.35))",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow:
                "0 2px 5px rgba(0, 48, 58, 0.32), inset 0 1px 2px rgba(0, 0, 0, 0.22)",
            },
            "&:focus-visible": {
              outline: "2px solid rgba(161, 241, 224, 0.82)",
              outlineOffset: 3,
            },
          }}
        >
          <Typography
            component="span"
            className="control-label"
            sx={{
              fontSize: "0.82rem",
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: 0,
              whiteSpace: "nowrap",
            }}
          >
            Start guide
          </Typography>
        </Box>
      )}

      {hasDisplayMessage && (
        <Box
          sx={{
            width: "fit-content",
            maxWidth: "min(280px, 100%)",
            boxSizing: "border-box",
            px: 2,
            py: 1,
            ...viewerPanelSurfaceSx,
            color: "var(--label-text, #f2fffc)",
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.85rem",
              lineHeight: 1.45,
              color: "var(--label-text, #f2fffc)",
              textAlign: "center",
              width: "auto",
            }}
          >
            {displayMessage}
          </Typography>
        </Box>
      )}

      {showRecordingUI && (
        <Box
          sx={{
            width: "100%",
            maxWidth: "min(280px, 100%)",
            boxSizing: "border-box",
            mt: hasDisplayMessage ? 0.75 : 0,
            px: 1.5,
            py: 1.1,
            ...viewerPanelSurfaceSx,
            color: "var(--label-text, #f2fffc)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.9,
            animation: "recFadeIn 0.2s ease",
            "@keyframes recFadeIn": {
              "0%":   { opacity: 0 },
              "100%": { opacity: 1 },
            },
          }}
        >
          {/* Row: mic dot + "Recording" label + waveform bars */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              "@keyframes recordBar": {
                "0%, 100%": { transform: "scaleY(0.35)", opacity: 0.45 },
                "50%":      { transform: "scaleY(1)",    opacity: 1    },
              },
            }}
          >
            {/* Pulsing mic dot */}
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.9)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                "@keyframes micRing": {
                  "0%":   { boxShadow: `0 0 0 0   rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.45)` },
                  "70%":  { boxShadow: `0 0 0 6px rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0)`    },
                  "100%": { boxShadow: `0 0 0 0   rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0)`    },
                },
                animation: "micRing 1.4s ease-out infinite",
              }}
            >
              <MicrophoneIcon width={12} height={12} />
            </Box>

            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.06em",
                color: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.95)`,
              }}
            >
              Recording
            </Typography>

            {/* Waveform bars */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 3,
                    height: 11,
                    borderRadius: "999px",
                    background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.8)`,
                    animation: "recordBar 0.85s ease-in-out infinite",
                    animationDelay: `${i * 0.13}s`,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* I'm done button */}
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onStopRecording();
            }}
            sx={{
              ...viewerActionButtonSx,
              width: "100%",
              minHeight: 36,
              px: 1.2,
              fontSize: "0.82rem",
            }}
          >
            <Typography
              component="span"
              className="control-label"
              sx={{
                ...viewerActionButtonLabelSx,
                fontSize: "0.82rem",
              }}
            >
              I&apos;m done
            </Typography>
          </Box>
        </Box>
      )}

      {showMicrophonePermissionButton && (
        <Box
          component="button"
          type="button"
          aria-label="Enable microphone"
          onClick={(event) => {
            event.stopPropagation();
            onEnableMicrophone();
          }}
          sx={{
            mt: 0.75,
            px: 1.2,
            py: 0.6,
            borderRadius: "999px",
            border: `1px solid rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.42)`,
            background: "rgba(0,0,0,0.42)",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.6,
            cursor: "pointer",
            fontFamily: "inherit",
            boxShadow: `0 8px 18px rgba(0,0,0,0.24), 0 0 14px rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.16)`,
            transition: "background 0.15s ease, border-color 0.15s ease, transform 0.15s ease",
            "& svg": {
              width: 14,
              height: 14,
            },
            "&:hover": {
              background: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.18)`,
              borderColor: `rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.65)`,
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(1px)",
            },
            "&:focus-visible": {
              outline: `2px solid rgba(${stateRgb.r}, ${stateRgb.g}, ${stateRgb.b}, 0.9)`,
              outlineOffset: 3,
            },
          }}
        >
          <MicrophoneIcon />
          <Typography
            component="span"
            className="control-label"
            sx={{
              fontSize: "0.76rem",
              fontWeight: 750,
              lineHeight: 1,
              color: "rgba(255,255,255,0.96)",
            }}
          >
            Enable microphone
          </Typography>
        </Box>
      )}


      {!showRecordingUI && questionEnabled && questionState === "error" && (
        <Typography
          sx={{
            mt: 0.65,
            fontSize: "0.8rem",
            color: "rgb(255, 255, 255)",
            letterSpacing: "0.01em",
            textAlign: "center",
          }}
        >
          Tap the orb to try your question again.
        </Typography>
      )}

      {!showRecordingUI && !questionEnabled && showWaitingHint && (
        <Typography
          sx={{
            mt: 0.65,
            fontSize: "0.8rem",
            color: "rgb(255, 255, 255)",
            letterSpacing: "0.01em",
            textAlign: "center",
          }}
        >
          Lost? Use Replay guide to hear it again.
        </Typography>
      )}

      <Box sx={{ height: 18, mt: 1 }} />
    </Box>
  );
};

// ============================================
// WRAPPER
// ============================================

const GuidePrototypes = ({
  guideState,
  guideStateText,
  startGuide,
  repeatInsight,
  isRecording = false,
  onStopRecording,
  questionState = "idle",
  questionEnabled = false,
  questionDisplayText = "",
  onAskQuestion,
  microphonePermissionDenied = false,
  onEnableMicrophone,
  defaultMessage = "",
  showStartGuideButton = false,
  guideAudioLevel = 0,
  microphoneLevel = 0,
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        pointerEvents: "auto",
        minWidth: 0,
      }}
    >
      <BlobPrototype
        guideState={guideState}
        guideStateText={guideStateText}
        onStartGuide={startGuide}
        onRepeatInsight={repeatInsight}
        isRecording={isRecording}
        onStopRecording={onStopRecording}
        questionState={questionState}
        questionEnabled={questionEnabled}
        questionDisplayText={questionDisplayText}
        onAskQuestion={onAskQuestion}
        microphonePermissionDenied={microphonePermissionDenied}
        onEnableMicrophone={onEnableMicrophone}
        defaultMessage={defaultMessage}
        showStartGuideButton={showStartGuideButton}
        guideAudioLevel={guideAudioLevel}
        microphoneLevel={microphoneLevel}
      />
    </Box>
  );
};

export default GuidePrototypes;
export { BlobPrototype };
